import { useState, useEffect, useCallback } from 'react';
import { useLang } from '../contexts/LangContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import TablePagination from '../components/TablePagination';

export default function CustomerPage() {
  const { t } = useLang();
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', notes: '', vehicle_number: '' });
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const loadCustomers = useCallback(async (page = 1) => {
    try {
      const res = await api.listCustomers(page, 20);
      setCustomers(res.data.data);
      setPagination({ total: res.data.total, page: res.data.page, limit: res.data.limit, total_pages: res.data.total_pages });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.updateCustomer(editing.id, form); }
      else { await api.createCustomer(form); }
      toast.success(t('success'));
      setDialogOpen(false); setEditing(null);
      setForm({ name: '', phone: '', notes: '', vehicle_number: '' });
      loadCustomers(pagination.page);
    } catch (err) { toast.error(err?.response?.data?.detail || t('error')); }
  };

  const handleEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, phone: c.phone || '', notes: c.notes || '', vehicle_number: c.vehicle_number || '' });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try { await api.deleteCustomer(deleteId); toast.success(t('success')); setDeleteId(null); loadCustomers(pagination.page); }
    catch (err) { toast.error(t('error')); }
  };

  const filtered = customers.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search) ||
    c.vehicle_number?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>{t('customers')}</h1>
          <p className="text-sm text-[#64584F]">{pagination.total} {t('customers').toLowerCase()}</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ name: '', phone: '', notes: '', vehicle_number: '' }); setDialogOpen(true); }} className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="add-customer-btn">
          <Plus size={16} className="mr-2" /> {t('add')} {t('customers')}
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A6A2A0]" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={`${t('search')}...`} className="pl-9 bg-[#F6F5F5] border-transparent focus:bg-white focus:border-[#EB7B44]" data-testid="customer-search-input" />
      </div>

      <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F6F5F5] border-b border-[#CACACC]">
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('name')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden sm:table-cell">{t('phone')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden md:table-cell">{t('vehicle_number')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden lg:table-cell">{t('notes')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider text-right">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-[#A6A2A0]">{t('loading')}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-[#A6A2A0]">{t('no_data')}</TableCell></TableRow>
            ) : filtered.map(c => (
              <TableRow key={c.id} className="border-b border-[#DFDEDE] hover:bg-[#F6F5F5]/50 transition-colors" data-testid={`customer-row-${c.id}`}>
                <TableCell className="font-medium text-[#202020]">{c.name}</TableCell>
                <TableCell className="text-[#64584F] hidden sm:table-cell">{c.phone || '-'}</TableCell>
                <TableCell className="text-[#64584F] hidden md:table-cell">{c.vehicle_number || '-'}</TableCell>
                <TableCell className="text-[#64584F] hidden lg:table-cell max-w-[200px] truncate">{c.notes || '-'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(c)}><Pencil size={15} className="text-[#64584F]" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(c.id)}><Trash2 size={15} className="text-red-500" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={pagination.page} totalPages={pagination.total_pages} total={pagination.total} limit={pagination.limit} onPageChange={(p) => loadCustomers(p)} />
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-md">
          <DialogHeader><DialogTitle className="text-[#202020]" style={{ fontFamily: 'Manrope' }}>{editing ? t('edit') : t('add')} {t('customers')}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('name')} *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="border-[#CACACC]" data-testid="customer-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('phone')}</Label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="border-[#CACACC]" data-testid="customer-phone-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('vehicle_number')}</Label>
              <Input value={form.vehicle_number} onChange={e => setForm({...form, vehicle_number: e.target.value})} placeholder="B 1234 XYZ" className="border-[#CACACC]" data-testid="customer-vehicle-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('notes')}</Label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="border-[#CACACC]" data-testid="customer-notes-input" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#CACACC]">{t('cancel')}</Button>
              <Button type="submit" className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="customer-save-btn">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-sm">
          <DialogHeader><DialogTitle className="text-[#202020]">{t('confirm_delete')}</DialogTitle></DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#CACACC]">{t('cancel')}</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-delete-customer-btn">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
